'use client';

import type { AnimationPlaybackControls } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@savvy-kit/lib/utils';
import { animate, motion, motionValue } from 'motion/react';
import { createPortal } from 'react-dom';

import type { ChangeKind, Direction } from './grading';
import type { Emphasis, FigureTokens } from './tokens';
import { GLYPH_CLASS, glyphOf } from './glyphs';
import { bezier, changeLevel, clampTravel, easingFor, milestoneCrossing, movingRange } from './grading';
import {
  COIN,
  COIN_FACE,
  COIN_SIZE_BY_TIER,
  DELAY_PERCENT,
  DIP_MS,
  EASE_SETTLE,
  EMPHASIS,
  FIGURE,
  INTENSITY_PERCENT,
} from './tokens';
import { useMotionReduced } from './use-motion-reduced';

type Anchor = { readonly left: number; readonly right: number; readonly cy: number };

type Coin = {
  readonly id: number;
  readonly anchor: 'left' | 'right' | 'center';
  readonly x: number;
  readonly y: number;
  readonly rot: number;
  readonly delay: number;
};

type CoinSprayProps = {
  readonly count: number;
  readonly size?: number;
  readonly rect: Anchor | null;
  readonly edges?: number;
};

// Coins leave from the two ends of the figure and arc outward, so the number itself looks like the source;
// anything beyond `edges` sprays out of the middle, which is what makes a milestone read as bigger rather than
// just longer. Deterministic offsets so server and client agree.
function layoutCoins(count: number, edges: number): readonly Coin[] {
  return Array.from({ length: count }, (_, i) => {
    if (i < edges) {
      const side = i % 2 === 0 ? -1 : 1;
      const k = Math.floor(i / 2);
      return {
        id: i,
        anchor: side < 0 ? 'left' : 'right',
        x: side * (46 + k * 26),
        y: -(30 + k * 14),
        rot: side * (130 + k * 40),
        delay: k * 0.05,
      };
    }
    const k = i - edges;
    const mid = count - edges;
    const t = mid === 1 ? 0.5 : k / (mid - 1);
    return {
      id: i,
      anchor: 'center',
      x: (t - 0.5) * 132,
      y: -(46 + ((k * 17) % 26)),
      rot: ((k * 83) % 300) - 150,
      delay: 0.04 + k * 0.03,
    };
  });
}

function anchorX(anchor: Coin['anchor'], rect: Anchor): number {
  if (anchor === 'left') return rect.left;
  if (anchor === 'right') return rect.right;
  return (rect.left + rect.right) / 2;
}

/** Coins thrown off by a milestone. Portalled to `<body>`: `position: fixed` resolves against a transformed ancestor, not the viewport. */
function CoinSpray({ count, size = COIN.size, rect, edges = COIN.edgesDefault }: CoinSprayProps) {
  const coins = useMemo(() => layoutCoins(count, edges), [count, edges]);
  if (rect === null || typeof document === 'undefined') return null;
  return createPortal(
    <span data-slot="coin-spray" className="pointer-events-none fixed inset-0 z-[70]">
      {coins.map((coin) => (
        <motion.span
          key={coin.id}
          aria-hidden
          className="absolute rounded-full"
          style={{
            left: anchorX(coin.anchor, rect) - size / 2,
            top: rect.cy - size / 2,
            width: size,
            height: size,
            ...COIN_FACE,
          }}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0.3 }}
          animate={{
            x: [0, coin.x * 0.7, coin.x],
            y: [0, coin.y, coin.y + 34],
            opacity: [0, 1, 0],
            scale: [0.3, 1, 0.62],
            rotate: [0, coin.rot * 0.5, coin.rot],
          }}
          transition={{ duration: COIN.flight, delay: coin.delay, times: [0, 0.42, 1], ease: [EASE_SETTLE, 'easeIn'] }}
        />
      ))}
    </span>,
    document.body,
  );
}

type Count = {
  readonly controls: AnimationPlaybackControls;
  readonly unsub: () => void;
  readonly target: number;
};

type GainNumberProps = {
  readonly value: number;
  /** What the figure is measured against: the value at the start of the selected range. */
  readonly from?: number;
  /** Re-fires the choreography when this changes, even if `value` did not. */
  readonly trigger?: string | number | boolean | null;
  readonly format?: (value: number) => string;
  readonly delay?: number;
  readonly direction?: Direction;
  readonly coins?: boolean;
  /** `currency` grades by dollars moved, `percent` by points, `relative` by proportion. */
  readonly kind?: ChangeKind;
  /** Hold a fixed width while the digits are moving so a changing figure cannot reflow the row. */
  readonly reserveWidth?: boolean;
  /** Count through the gap from `from` instead of snapping at the bottom of the dip. */
  readonly countThrough?: boolean;
  /** How hard this figure celebrates; the headline sits back so the percentage stays loudest. */
  readonly intensity?: number;
  readonly emphasis?: Emphasis;
  /** Per-figure overrides of the FIGURE bounce tokens. */
  readonly bounce?: Partial<FigureTokens> | null;
  /** Shorten the first-load count so the balance settles rather than spins. */
  readonly capTravel?: boolean;
  /** Render the figure's own `sr-only` copy. `false` when a parent supplies the accessible text for a composite. */
  readonly labelled?: boolean;
  readonly className?: string;
};

function defaultFormat(value: number): string {
  return String(Math.round(value));
}

/**
 * A figure that changes with choreography rather than by counting: the digits dip, the value switches at the
 * bottom of the dip, and they spring back with a small overshoot. How far it dips scales with how big the change
 * was; crossing a milestone deepens it further and throws coins.
 */
function GainNumber({
  value,
  from,
  trigger,
  format = defaultFormat,
  delay = 0,
  direction,
  coins = true,
  kind = 'currency',
  reserveWidth = false,
  countThrough = false,
  intensity = 1,
  emphasis,
  bounce = null,
  capTravel = true,
  labelled = true,
  className,
}: GainNumberProps) {
  const safe = Number.isFinite(value) ? value : 0;
  const hasFrom = from != null && Number.isFinite(from);
  const reduced = useMotionReduced();
  const [play, setPlay] = useState(0);
  const playAtRef = useRef(0);
  const [armed, setArmed] = useState(false);
  // Holds the figure on screen; it lags `value` by exactly one dip. A count from somewhere starts at the count's
  // start, or the answer flashes before the count.
  const initialShown = countThrough && hasFrom ? (capTravel ? clampTravel(safe, from) : from) : safe;
  const [shown, setShown] = useState(initialShown);

  const hostRef = useRef<HTMLSpanElement>(null);
  const countRef = useRef<Count | null>(null);
  // Cancellable from the effect cleanup: orphaned dip timers each fired their own count and coin spray.
  const swapRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sprayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [spraying, setSpraying] = useState(false);
  const [settling, setSettling] = useState(false);
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  const shownRef = useRef(initialShown);
  const show = useCallback((next: number) => {
    shownRef.current = next;
    setShown(next);
  }, []);

  const cleanupCount = useCallback(
    (settle = true) => {
      const count = countRef.current;
      if (count === null) return;
      count.controls.stop();
      count.unsub();
      if (settle) show(count.target);
      countRef.current = null;
    },
    [show],
  );

  const prevRef = useRef(safe);
  // Graded off the CAPPED start, the same one the count runs from: whatever the figure visibly travels is what
  // it is allowed to celebrate.
  const reference = hasFrom ? (capTravel ? clampTravel(safe, from) : from) : prevRef.current;
  const tier = milestoneCrossing(reference, safe);
  // The size of the gesture follows the REAL change, so a big change looks big even when its count is capped.
  const magnitudeFrom = hasFrom ? from : prevRef.current;
  const level = changeLevel(magnitudeFrom, safe, kind);
  const span = level.amp;
  const tokens: FigureTokens = bounce === null ? FIGURE : { ...FIGURE, ...bounce };
  // One dial spent on every channel a figure has, so a large change is bigger in all of them at once.
  const k = tokens.gradient;
  const grade = Math.max(0.15, 1 + k * (1.2 * span - 0.4));
  const stretch = 1 + k * 0.35 * span;
  const spread = Math.max(0.2, 1 + k * (0.6 * span - 0.2));
  const dur = tier === null ? 0.42 : tier.duration;
  const charSpan = (() => {
    const [first, last] = movingRange(Array.from(format(safe)));
    const reach = first < 0 ? 0 : tokens.origin === 'center' ? (last - first) / 2 : last - first;
    return Math.max(0, reach * tokens.charStagger * spread);
  })();

  // Read when the choreography fires rather than listed as deps. These are per-figure settings; a parent that
  // re-renders with a fresh object must not restart a count mid-flight, and the invariant is enforced here
  // instead of asserted in a comment over a disabled lint rule.
  const settingsRef = useRef({ capTravel, countThrough, span, direction });
  settingsRef.current = { capTravel, countThrough, span, direction };

  useEffect(() => {
    if (reduced) {
      show(safe);
      prevRef.current = safe;
      return;
    }
    const startAt = setTimeout(() => {
      setArmed(true);
      if (swapRef.current !== null) {
        clearTimeout(swapRef.current);
        swapRef.current = null;
      }
      const rect = hostRef.current?.getBoundingClientRect();
      if (rect !== undefined) setAnchor({ left: rect.left, right: rect.right, cy: rect.top + rect.height / 2 });
      playAtRef.current = performance.now();
      setPlay((n) => n + 1);
      setSettling(true);
      if (sprayRef.current !== null) clearTimeout(sprayRef.current);
      setSpraying(true);
      sprayRef.current = setTimeout(() => {
        setSpraying(false);
        sprayRef.current = null;
      }, 1500);
      swapRef.current = setTimeout(() => {
        const settings = settingsRef.current;
        const countFrom = hasFrom ? (settings.capTravel ? clampTravel(safe, from) : from) : shownRef.current;
        if (settings.countThrough && Number.isFinite(countFrom) && countFrom !== safe) {
          cleanupCount(false);
          const cv = motionValue(countFrom);
          // Step, don't spin: landings scale UP with the size of the jump, but a count is never continuous.
          const steps = Math.min(12, Math.max(6, Math.round(6 + settings.span * 6)));
          const quantise = (v: number) => {
            const gap = safe - countFrom;
            if (gap === 0) return safe;
            const t = (v - countFrom) / gap;
            return countFrom + gap * (Math.round(t * steps) / steps);
          };
          const unsub = cv.on('change', (v) => show(quantise(v)));
          const controls = animate(cv, safe, {
            // With a `from`, the count's length follows the size of the change; changing in place, it runs
            // inside the bounce so the two read as one event.
            duration: hasFrom
              ? Math.max(0.35, 0.7 * (1 + k * (0.55 * settings.span - 0.15)))
              : Math.max(0.2, dur * stretch + charSpan - DIP_MS / 1000),
            // Easing follows the direction of TRAVEL, not the sign of the figure.
            ease: easingFor(settings.direction ?? (safe >= countFrom ? 'up' : 'down')),
            onComplete: () => {
              show(safe);
              setSettling(false);
            },
          });
          countRef.current = { controls, unsub, target: safe };
        } else {
          cleanupCount(false);
          show(safe);
          setSettling(false);
        }
        prevRef.current = safe;
        swapRef.current = null;
      }, DIP_MS);
    }, delay * 1000);
    return () => {
      clearTimeout(startAt);
      if (swapRef.current !== null) {
        clearTimeout(swapRef.current);
        swapRef.current = null;
      }
      if (sprayRef.current !== null) {
        clearTimeout(sprayRef.current);
        sprayRef.current = null;
      }
      cleanupCount();
      setSettling(false);
    };
  }, [safe, trigger, from, delay, reduced, show, cleanupCount]);

  // The constant term is the entrance hop, which has to stand on its own: the supporting stats never change
  // value, so span is 0 for them. Everything above it is earned by an actual change. A figure that moved down
  // barely moves on screen.
  const downward = safe < reference ? tokens.downward : 1;
  const cap = (v: number) => Math.max(-tokens.maxEm, Math.min(tokens.maxEm, v));
  const dip = cap(
    (tokens.dipBase + tokens.dipPerSpan * span) * intensity * grade * (tier === null ? 1 : tokens.tierDip) * downward,
  );
  const rise = cap(
    -(tokens.riseBase + tokens.risePerSpan * span) *
      intensity *
      grade *
      (tier === null ? 1 : tokens.tierRise) *
      downward,
  );
  const rising = safe > reference;
  const coinCount = !rising
    ? 0
    : tier !== null
      ? Math.max(level.coins, Math.min(tier.coins, Math.round(4 + (tier.coins - 4) * span)))
      : !capTravel
        ? level.coins
        : 0;
  const coinEdges = tier === null ? COIN.edgesSmall : COIN.edgesDefault;
  const coinSize =
    tier === null ? Math.round(COIN.sizeSmall + (COIN.size - COIN.sizeSmall) * span) : COIN_SIZE_BY_TIER[tier.id];

  const text = format(shown);
  const chars = Array.from(text);
  const widest = Math.max(format(reference).length, format(safe).length);
  const [firstMoving, lastMoving] = movingRange(chars);
  const digitCount = Math.max(1, lastMoving - firstMoving + 1);
  const mode = emphasis ?? EMPHASIS;
  // A glyph that mounts part-way through a count joins the bounce in phase rather than starting its own.
  const late = playAtRef.current === 0 ? 0 : Math.max(0, (performance.now() - playAtRef.current) / 1000);
  const stagger = kind === 'percent' ? tokens.charStaggerPercent : tokens.charStagger;
  let digitIndex = -1;

  return (
    <span
      ref={hostRef}
      data-slot="gain-number"
      style={reserveWidth && settling ? { minWidth: `${widest}ch` } : undefined}
      className={cn('relative inline-flex tabular-nums', className)}
    >
      {labelled && <span className="sr-only">{format(safe)}</span>}
      {/* Mounted whether or not it has anything to say, so the text arriving is a change a screen reader
          announces. The coins are aria-hidden, so a milestone has no non-visual signal without this. */}
      <span role="status" aria-live="polite" className="sr-only">
        {coins && coinCount > 0 && tier !== null && armed ? `Milestone reached: ${format(safe)}` : ''}
      </span>
      {chars.map((char, i) => {
        const moves = firstMoving >= 0 && i >= firstMoving && i <= lastMoving;
        if (moves) digitIndex += 1;
        const t = digitIndex / Math.max(1, digitCount - 1);
        const w = !moves
          ? 1
          : mode === 'leading'
            ? 1 - tokens.taper * t
            : mode === 'trailing'
              ? 1 - tokens.taper * (1 - t)
              : 1;
        const slot =
          tokens.origin === 'right'
            ? digitCount - 1 - digitIndex
            : tokens.origin === 'center'
              ? Math.abs(digitIndex - (digitCount - 1) / 2)
              : digitIndex;
        const still = reduced || !armed || !moves;
        return (
          <motion.span
            // Keyed on the play counter: the headline's target is identical on every range switch, so motion
            // would otherwise see nothing to replay.
            key={`${i}-${play}`}
            aria-hidden
            data-glyph={glyphOf(char)}
            className={GLYPH_CLASS}
            // Percent of the glyph's own box, not em: the hop can never exceed a fraction of the character it
            // belongs to, at any type size.
            animate={
              still
                ? { y: 0, scale: 1 }
                : {
                    y: [0, `${dip * w * 100}%`, `${rise * w * 100}%`, 0],
                    scale: [1, 1 - 0.03 * w, 1 + 0.06 * intensity * w * grade, 1],
                  }
            }
            transition={
              still
                ? { duration: 0 }
                : {
                    duration: tokens.bounce * stretch,
                    delay: slot * stagger * spread - late,
                    times: [0, DIP_MS / 340, 0.72, 1],
                    ease: bezier(tokens.ease),
                  }
            }
            style={{ transformOrigin: 'center bottom' }}
          />
        );
      })}
      {coins && coinCount > 0 && !reduced && armed && spraying && (
        <CoinSpray key={play} count={coinCount} edges={coinEdges} size={coinSize} rect={anchor} />
      )}
    </span>
  );
}

type PercentDeltaProps = {
  readonly value: number;
  readonly trigger?: GainNumberProps['trigger'];
  readonly delay?: number;
  readonly format?: (value: number) => string;
  readonly from?: number;
  readonly capTravel?: boolean;
  readonly labelled?: boolean;
  readonly className?: string;
};

/** The percentage treatment, in one place: the loudest of the three figures, damped on the way down. */
function PercentDelta({
  value,
  trigger,
  delay = DELAY_PERCENT,
  format,
  from,
  capTravel,
  labelled,
  className,
}: PercentDeltaProps) {
  return (
    <GainNumber
      value={value}
      from={from}
      capTravel={capTravel}
      trigger={trigger}
      countThrough
      kind="percent"
      reserveWidth
      coins={false}
      intensity={INTENSITY_PERCENT}
      delay={delay}
      format={format}
      labelled={labelled}
      className={className}
    />
  );
}

export { GainNumber, PercentDelta };
export type { GainNumberProps, PercentDeltaProps };
